import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import { ActionIcon, Center, Group, Stack, Title, Tooltip } from "@mantine/core"
import { IconArrowBack, IconChartPie, IconEdit, IconFileAnalytics, IconFileDescription, IconFilter } from '@tabler/icons-react'
import { AdvancedReporting, Charts, DataElementsModal, DownloadReport, FiltersModal, ReportParameters } from '../../components/Reports'
import { Table } from '../../components/common/Table'
import { useReport } from '../../hooks/reports/use-report'

const ReportContainer = ({ launchDarklyFeatureFlags }: { launchDarklyFeatureFlags: Record<string, boolean> | undefined }) => {
    const params: { reportPk: string | undefined } = useParams()
    const reportPk = (params?.reportPk as string)
    const history = useHistory()
    const [view, setView] = useState<'results'|'charts'|'advancedr'>('results')
    const location = useLocation()

    const {
        state,
        reportNotFound,
        elementTree,
        elementTreeLoading,
        reportLoading,
        isPreviousData,
        actions
    } = useReport(Number(reportPk))

    const { config, creatingColumnsWithData, page, limit, selectedElements, tableData, dataElementsModalOpen, filtersModalOpen, sorters } = state

    const closeWithoutSaving = () => {
        actions.resetSelectedElements()
        actions.setDataElementsModalOpen(false)
    }

    const isEnabled = launchDarklyFeatureFlags && launchDarklyFeatureFlags['rw-react'] || launchDarklyFeatureFlags && !Object.keys(launchDarklyFeatureFlags).length
    useEffect(() => {
        if (!isEnabled) history.replace(`/iframe/app/#reportWriter/${reportPk}`)
    },[isEnabled])

    useEffect(() => {
        if (location.search.includes('showDataElements')) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('showDataElements');
            history.push({
                pathname: location.pathname,
            });
            actions.setDataElementsModalOpen(true)
        }
    }, [location.search])

    if (!isEnabled) return <></>

    if (reportNotFound) return <Center mt={50}><Title order={4}>No Report Found</Title></Center>

    return (
        <>
            <DataElementsModal
                opened={dataElementsModalOpen}
                selectedElements={selectedElements}
                setSelectedElements={actions.setSelectedElements}
                handleSave={() => actions.createColumnsWithData()}
                elementTree={elementTree}
                elementTreeLoading={elementTreeLoading}
                creatingColumnsWithData={creatingColumnsWithData}
                toggleElementSelection={actions.toggleElementSelection}
                removeElement={actions.removeElement}
                onClose={closeWithoutSaving}
                moveItem={actions.moveItem}
            />
            <FiltersModal opened={filtersModalOpen} onClose={() => actions.setFiltersModalOpen(false)} />
            <Stack gap={0}>
                <Group p={15} justify='space-between'>
                    <Title order={3} fw='normal' onClick={() => actions.loadData()}>{config?.params.report_name}</Title>
                    <Group>
                    <ActionIcon.Group>
                    <Tooltip label="Report Results">
                        <ActionIcon size='xl'  variant={view === 'results' ? 'filled' : 'outline'} onClick={() => setView('results')}>
                            <IconFileDescription style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Charts">
                        <ActionIcon size='xl'  variant={view === 'charts' ? 'filled' : 'outline'} onClick={() => setView('charts')}>
                            <IconChartPie style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Advanced Reporting">
                        <ActionIcon size='xl'  variant={view === 'advancedr' ? 'filled' : 'outline'} onClick={() => setView('advancedr')}>
                            <IconFileAnalytics style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Data Elements">
                        <ActionIcon size='xl'  variant="outline" onClick={() => actions.setDataElementsModalOpen(true)}>
                            <IconEdit style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Filters">
                        <ActionIcon size='xl'  variant="outline" onClick={() => actions.setFiltersModalOpen(true)}>
                            <IconFilter style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                    { config?.params &&
                    <ReportParameters parameters={config.params} updateParams={actions.updateReportParams} /> }
                    { config &&
                    <DownloadReport reportPk={reportPk} columns={tableData.columns} config={config} /> }
                    </ActionIcon.Group>
                    <Tooltip label="Report Library">
                        <ActionIcon size='xl'  variant='transparent' onClick={() => history.push('/reports/library')}>
                            <IconArrowBack style={{ width: '70%', height: '70%' }} stroke={1.5} />
                        </ActionIcon>
                    </Tooltip>
                    </Group>
                </Group>
                { view === 'results' &&
                <Table
                    rows={tableData?.rows}
                    columns={tableData?.columns}
                    sorters={sorters}
                    h='calc(100vh - 140px)'
                    onSort={(sort) => actions.setSorters([sort])}
                    rowCount={tableData?.rowCount}
                    page={page}
                    limit={limit}
                    setPage={actions.setPage}
                    setLimit={actions.selectLimit}
                    loading={reportLoading || isPreviousData}
                />
                }
                { view === 'charts' && <Charts /> }
                { view === 'advancedr' && <AdvancedReporting /> }
            </Stack>
        </>
    )
}

export default ReportContainer
